/**
 * Point-level editing (MAP-7). Pure helpers for adding and removing a single
 * point in the editor's in-memory PointSet — the SAME structure Save serializes
 * into the published snapshot (buildSnapshot → toGeoJSON), so a manually added
 * point is a REAL dataset row: it flows through Save / share / export / viewer /
 * insights unchanged, with no parallel data model.
 *
 * A manual point carries a deterministic `row_key` derived the same way ingest
 * derives it (rowKeyForAddress over the normalized query), so it is cache- and
 * dedupe-coherent with the rest of the dataset and stable across a re-render.
 *
 * Browser-safe (js-sha256 via rowKey, no node:crypto, no parser deps) so the
 * editor imports it freely.
 */

import { rowKeyForAddress } from "@/lib/ingest/rowKey";
import { boundsOf, type Point, type PointColumn, type PointSet } from "./pointset";

export interface SynthesizePointArgs {
  /** The address/name the user typed — the geocode query and the row's identity. */
  query: string;
  lat: number;
  lng: number;
  /** 0..1 geocode confidence; a hand-placed point defaults to 1. */
  confidence?: number;
  /** Existing dataset columns — every one is seeded empty on the new row. */
  columns: readonly PointColumn[];
  /** The column the typed address is written into (other columns stay empty). */
  addressColumn: string;
}

/**
 * Synthesize a dataset row for a manually added place: geocoded geometry, the
 * typed text in `addressColumn`, every other existing column empty. The id is
 * the deterministic row_key of the query, so the point is identical to what an
 * ingest of the same address would produce (M2 cache + re-map diff coherent).
 */
export function synthesizePoint(args: SynthesizePointArgs): Point {
  const props: Record<string, string> = {};
  for (const col of args.columns) props[col.name] = "";
  props[args.addressColumn] = args.query;
  return {
    id: rowKeyForAddress(args.query),
    lat: args.lat,
    lng: args.lng,
    confidence: args.confidence ?? 1,
    props,
  };
}

/**
 * Append a point to the set, recomputing bounds + count and folding the point's
 * property keys into the column list (a value under a brand-new key adds that
 * column; a value under an existing key extends its distinct sample). Columns
 * are copied, never mutated in place, so the previous set stays intact.
 */
export function addPointToSet(set: PointSet, point: Point): PointSet {
  const columns: PointColumn[] = set.columns.map((c) => ({ ...c, distinctSample: [...c.distinctSample] }));
  const byName = new Map(columns.map((c) => [c.name, c]));
  for (const [key, value] of Object.entries(point.props)) {
    let col = byName.get(key);
    if (!col) {
      col = { name: key, type: "text", distinctSample: [] };
      columns.push(col);
      byName.set(key, col);
    }
    if (value !== "" && !col.distinctSample.includes(value) && col.distinctSample.length < 8) {
      col.distinctSample.push(value);
    }
  }
  const points = [...set.points, point];
  return { columns, points, bounds: boundsOf(points), count: points.length };
}

/**
 * Remove exactly the point with `id`, recomputing bounds + count. Removing a
 * missing id is a no-op that returns the SAME set reference (no needless
 * re-render). Columns are left as-is — a removed row never removes a column.
 */
export function removePointFromSet(set: PointSet, id: string): PointSet {
  const points = set.points.filter((p) => p.id !== id);
  if (points.length === set.points.length) return set;
  return { ...set, points, bounds: boundsOf(points), count: points.length };
}

/**
 * Re-apply this session's manual edits (added points + removed ids) on top of a
 * freshly rebuilt PointSet. Called after "Update map" re-geocodes from the
 * imported grid, so hand edits survive a re-make instead of being silently
 * dropped. Removals are applied first, then adds whose id isn't already present
 * (an added address that now also exists in the import is not duplicated).
 */
export function applyManualEdits(
  base: PointSet,
  added: readonly Point[],
  removedIds: ReadonlySet<string>,
): PointSet {
  let result = base;
  if (removedIds.size > 0) {
    const points = base.points.filter((p) => !removedIds.has(p.id));
    result = { ...base, points, bounds: boundsOf(points), count: points.length };
  }
  for (const p of added) {
    if (!result.points.some((q) => q.id === p.id)) result = addPointToSet(result, p);
  }
  return result;
}

export interface AddGuard {
  ok: boolean;
  message?: string;
}

/**
 * Free-tier / global row-ceiling guard at the point of add — the same ceiling
 * ingest enforces on import (checkRowCeiling / HARD_ROW_CEILING), applied here
 * so adding points one at a time can't slip past it. The server's snapshot
 * feature cap is the authoritative backstop on Save.
 */
export function canAddPoint(currentCount: number, ceiling: number): AddGuard {
  if (currentCount >= ceiling) {
    return {
      ok: false,
      message: `This map is at its ${ceiling.toLocaleString()}-point limit. Remove a place before adding another.`,
    };
  }
  return { ok: true };
}

/**
 * Human label for a point in the places list: the popup title column's value if
 * present, else the first non-empty property, else its rounded coordinates.
 */
export function placeLabel(point: Point, titleColumn?: string): string {
  const title = titleColumn ? point.props[titleColumn]?.trim() : "";
  if (title) return title;
  for (const value of Object.values(point.props)) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return `Place at ${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`;
}
